/**
 * Locale Selector Component
 * Dropdown to select language and currency
 */

import { useState, useRef, useEffect } from 'react';
import { Globe, ChevronDown, Check } from 'lucide-react';
import useLocaleStore, { LANGUAGES, CURRENCIES, COUNTRIES } from '../../store/useLocaleStore';

const LocaleSelector = ({ variant = 'full', className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('language');
  const dropdownRef = useRef(null);
  
  const { language, currency, country, setLanguage, setCurrency, setCountry } = useLocaleStore();
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const currentLanguage = LANGUAGES[language];
  const currentCurrency = CURRENCIES[currency];
  const currentCountry = COUNTRIES[country];
  
  if (variant === 'compact') {
    return (
      <div className={`relative ${className}`} ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          data-testid="locale-selector-btn"
          aria-label={`Idioma: ${currentLanguage?.name}, Moeda: ${currentCurrency?.code}`}
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-full border border-gray-200 hover:border-gray-300 transition"
        >
          <span className="text-base leading-none" aria-hidden="true">{currentLanguage?.flag}</span>
          <span className="font-medium text-gray-900 uppercase tracking-wide">{currentLanguage?.code?.split('-')[0] || 'PT'}</span>
          <span className="text-gray-300 text-xs">•</span>
          <span className="text-xs text-gray-500 font-normal">{currentCurrency?.symbol}</span>
          <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        
        {isOpen && (
          <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-100 z-50 overflow-hidden" data-testid="locale-dropdown">
            <div className="p-3">
              {/* Language — destaque principal */}
              <div className="flex items-center gap-1.5 px-1 mb-2">
                <Globe className="w-3.5 h-3.5 text-blue-600" />
                <span className="text-xs font-semibold text-gray-900 uppercase tracking-wider">Idioma</span>
              </div>
              <div className="space-y-0.5 mb-3">
                {Object.values(LANGUAGES).map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => { setLanguage(lang.code); setIsOpen(false); }}
                    className={`w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-sm transition ${
                      language === lang.code ? 'bg-blue-50 text-blue-700 ring-1 ring-blue-200' : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span className="text-lg leading-none">{lang.flag}</span>
                    <span className="flex-1 text-left font-medium">{lang.name}</span>
                    {language === lang.code && <Check className="w-4 h-4 text-blue-600" />}
                  </button>
                ))}
              </div>
              
              {/* Currency — subtil e compacto */}
              <div className="pt-2 border-t border-gray-100">
                <div className="flex items-center justify-between px-1 mb-1.5">
                  <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">Moeda</span>
                </div>
                <div className="grid grid-cols-3 gap-1">
                  {Object.values(CURRENCIES).map((curr) => (
                    <button
                      key={curr.code}
                      onClick={() => { setCurrency(curr.code); setIsOpen(false); }}
                      className={`flex flex-col items-center px-2 py-1.5 rounded-md text-xs transition ${
                        currency === curr.code ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                      }`}
                      title={curr.name}
                    >
                      <span className="font-semibold">{curr.symbol}</span>
                      <span className="text-[10px] opacity-75">{curr.code}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
  
  // Full variant with tabs
  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 bg-white hover:bg-gray-50 border border-gray-200 rounded-xl transition"
      >
        <Globe className="w-4 h-4" />
        <span>{currentLanguage?.flag} {currentLanguage?.name?.split(' ')[0]}</span>
        <span className="text-gray-400">|</span>
        <span>{currentCurrency?.symbol} {currentCurrency?.code}</span>
        <ChevronDown className={`w-4 h-4 transition ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute right-0 mt-2 w-72 bg-white rounded-2xl shadow-xl border border-gray-100 z-50 overflow-hidden">
          {/* Tabs */}
          <div className="flex border-b border-gray-100">
            {[
              { id: 'language', label: 'Idioma' },
              { id: 'currency', label: 'Moeda' },
              { id: 'country', label: 'País' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 py-3 text-sm font-medium transition ${
                  activeTab === tab.id 
                    ? 'text-blue-600 border-b-2 border-blue-600' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          
          <div className="p-3 max-h-64 overflow-y-auto">
            {/* Language Tab */}
            {activeTab === 'language' && (
              <div className="space-y-1">
                {Object.values(LANGUAGES).map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => { setLanguage(lang.code); }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition ${
                      language === lang.code 
                        ? 'bg-blue-50 text-blue-600 border border-blue-200' 
                        : 'text-gray-700 hover:bg-gray-50 border border-transparent'
                    }`}
                  >
                    <span className="text-lg">{lang.flag}</span>
                    <span className="flex-1 text-left font-medium">{lang.name}</span>
                    {language === lang.code && <Check className="w-4 h-4" />}
                  </button>
                ))}
              </div>
            )}
            
            {/* Currency Tab */}
            {activeTab === 'currency' && (
              <div className="space-y-1">
                {Object.values(CURRENCIES).map((curr) => (
                  <button
                    key={curr.code}
                    onClick={() => { setCurrency(curr.code); }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition ${
                      currency === curr.code 
                        ? 'bg-blue-50 text-blue-600 border border-blue-200' 
                        : 'text-gray-700 hover:bg-gray-50 border border-transparent'
                    }`}
                  >
                    <span className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-lg font-bold text-gray-700">
                      {curr.symbol}
                    </span>
                    <div className="flex-1 text-left">
                      <p className="font-medium">{curr.code}</p>
                      <p className="text-xs text-gray-500">{curr.name}</p>
                    </div>
                    {currency === curr.code && <Check className="w-4 h-4" />}
                  </button>
                ))}
              </div>
            )}
            
            {/* Country Tab */}
            {activeTab === 'country' && (
              <div className="space-y-1">
                {Object.values(COUNTRIES).map((ctry) => (
                  <button
                    key={ctry.code}
                    onClick={() => { setCountry(ctry.code); }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition ${
                      country === ctry.code 
                        ? 'bg-blue-50 text-blue-600 border border-blue-200' 
                        : 'text-gray-700 hover:bg-gray-50 border border-transparent'
                    }`}
                  >
                    <span className="text-lg">{ctry.flag}</span>
                    <div className="flex-1 text-left">
                      <p className="font-medium">{ctry.name}</p>
                      <p className="text-xs text-gray-500">{CURRENCIES[ctry.currency]?.symbol} {ctry.currency}</p>
                    </div>
                    {country === ctry.code && <Check className="w-4 h-4" />}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {/* Current Selection */}
          <div className="p-3 bg-gray-50 border-t border-gray-100">
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>Atual: {currentCountry?.flag} {currentLanguage?.name?.split(' ')[0]} • {currentCurrency?.symbol}</span>
              <button 
                onClick={() => setIsOpen(false)}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocaleSelector;
